/**
 * The page editor's rules, as pure functions — T-4.33, T-4.34.
 *
 * The shape `lib/api/firm.ts` set in Wave 1A and `lib/api/site.ts` kept: the
 * decisions live here, testable without rendering anything, and `PageEditor`
 * is the screen that draws them.
 *
 * **Nothing here describes a block.** Fields, labels, limits and choices all
 * come from `GET /api/v1/site/vocabulary`, which is `lib/api/site.ts`'s rule 2
 * and the reason thirteen new sections reached the builder's forms without a
 * line of TypeScript (ADR-0022 §1). What is here is what the *editor* knows
 * and the service does not: that a list can be reordered, that a delete can be
 * taken back, and the facts about a section that a firm cannot see by looking
 * at it — of which Wave 8H added a fourth, `emptyCatalogueNote`.
 */

import type { BlockContext } from '../../../site/blocks/common'
import { whyHidden } from '../../../site/blocks/visibility'
import { themeSections } from '../../../site/theme-sections'
import { blankBlock, type BlockSchema, type BlockType, type Vocabulary } from '../../lib/api/site'

// -- what the editor holds ---------------------------------------------------

/**
 * One section, while it is being edited.
 *
 * **`uid` is why this type exists.** The editor keyed its list by array index
 * until this session, which React reads as "the third card is the third card":
 * move a section and the open/closed state, the scroll position and every
 * uncontrolled input inside the form stay behind with the index while the
 * content moves. A stable key per section is the whole fix, and it is also what
 * lets focus follow a moved card (T-4.35) — there is nothing else to point at,
 * because a section has no id until it is saved.
 */
export interface EditorBlock {
  uid: string
  type: BlockType
  props: Record<string, unknown>
}

export interface Removed {
  block: EditorBlock
  index: number
  /** What to call it in "Removed X." — the picker's label, so the undo line and
   *  the card a firm just deleted say the same word. */
  label: string
}

export interface SectionsState {
  blocks: EditorBlock[]
  /** Whether the list differs from what was loaded or last saved. */
  dirty: boolean
  /** The last delete, until it is undone, superseded or saved away. */
  removed: Removed | null
  /** What a screen reader should hear about the last change. Reordering with
   *  the keyboard is silent otherwise: the list moves and nothing says so. */
  announcement: string
}

export const EMPTY: SectionsState = { blocks: [], dirty: false, removed: null, announcement: '' }

export type SectionsAction =
  /** A page arrived from the service, or a save came back. */
  | { kind: 'load'; blocks: { type: BlockType; props: Record<string, unknown> }[] }
  /** `uid` is the caller's so it can open the new section straight away —
   *  there is nothing else to point at until the next render. */
  | { kind: 'add'; uid: string; schema: BlockSchema; label: string; at?: number }
  /** A whole section, from a draft the firm accepted. */
  | { kind: 'insert'; uid: string; type: BlockType; props: Record<string, unknown>; label: string }
  | { kind: 'move'; uid: string; to: number; label: string }
  | { kind: 'duplicate'; uid: string; label: string }
  | { kind: 'remove'; uid: string; label: string }
  | { kind: 'undo' }
  | { kind: 'dismissUndo' }
  | { kind: 'edit'; uid: string; props: Record<string, unknown> }

let counter = 0
/** A key for one section in one editing session. Never stored, never sent. */
export function uid(): string {
  counter += 1
  return `s${counter}`
}

/** Where a section sits, said the way a person would: "3 of 7". */
export function position(index: number, total: number): string {
  return `${index + 1} of ${total}`
}

export function sectionsReducer(state: SectionsState, action: SectionsAction): SectionsState {
  switch (action.kind) {
    case 'load':
      return {
        blocks: action.blocks.map((b) => ({ uid: uid(), type: b.type, props: { ...b.props } })),
        dirty: false,
        // **The undo does not survive a save.** What it would restore is a
        // section the service has already been told to forget, and offering it
        // afterwards offers a firm something that no longer means what it says.
        removed: null,
        announcement: '',
      }

    case 'add':
    case 'insert': {
      const block: EditorBlock =
        action.kind === 'add'
          ? { ...blankBlock(action.schema), uid: action.uid }
          : { uid: action.uid, type: action.type, props: { ...action.props } }
      const at = action.kind === 'add' && action.at !== undefined ? action.at : state.blocks.length
      const index = Math.max(0, Math.min(at, state.blocks.length))
      const blocks = [...state.blocks]
      blocks.splice(index, 0, block)
      return {
        ...state,
        blocks,
        dirty: true,
        announcement: `${action.label} added, ${position(index, blocks.length)}.`,
      }
    }

    case 'move': {
      const from = state.blocks.findIndex((b) => b.uid === action.uid)
      const moved = state.blocks[from]
      if (!moved || action.to < 0 || action.to >= state.blocks.length || action.to === from) {
        return state
      }
      const blocks = [...state.blocks]
      blocks.splice(from, 1)
      blocks.splice(action.to, 0, moved)
      return {
        ...state,
        blocks,
        dirty: true,
        announcement: `${action.label} moved to ${position(action.to, blocks.length)}.`,
      }
    }

    case 'duplicate': {
      const index = state.blocks.findIndex((b) => b.uid === action.uid)
      const source = state.blocks[index]
      if (!source) return state
      const blocks = [...state.blocks]
      // **Beside it, not at the end.** A firm duplicating the fourth of nine
      // sections wants a second one there; putting the copy at the bottom
      // makes duplicate-then-drag the only way to use it.
      //
      // A shallow copy of `props` would share every `items` array with the
      // original, so editing one row of the copy would edit the same row of
      // the section it came from.
      blocks.splice(index + 1, 0, { uid: uid(), type: source.type, props: deepCopy(source.props) })
      return {
        ...state,
        blocks,
        dirty: true,
        announcement: `${action.label} duplicated, ${position(index + 1, blocks.length)}.`,
      }
    }

    case 'remove': {
      const index = state.blocks.findIndex((b) => b.uid === action.uid)
      const block = state.blocks[index]
      if (!block) return state
      return {
        blocks: state.blocks.filter((b) => b.uid !== action.uid),
        dirty: true,
        removed: { block, index, label: action.label },
        announcement: `${action.label} removed. It can be put back.`,
      }
    }

    case 'undo': {
      const { removed } = state
      if (!removed) return state
      const blocks = [...state.blocks]
      // Back where it was, and at the end if the list shrank under it — never
      // dropped on the floor because the index no longer fits.
      const index = Math.max(0, Math.min(removed.index, blocks.length))
      blocks.splice(index, 0, removed.block)
      return {
        blocks,
        dirty: true,
        removed: null,
        announcement: `${removed.label} put back, ${position(index, blocks.length)}.`,
      }
    }

    case 'dismissUndo':
      return state.removed ? { ...state, removed: null } : state

    case 'edit': {
      const blocks = state.blocks.map((b) =>
        b.uid === action.uid ? { ...b, props: action.props } : b,
      )
      return { ...state, blocks, dirty: true }
    }
  }
}

/** A section's own content, copied rather than shared — `items` arrays and the
 *  objects inside them, which is where a shallow copy leaks. */
function deepCopy<T>(value: T): T {
  if (Array.isArray(value)) return value.map((v) => deepCopy(v)) as unknown as T
  if (value && typeof value === 'object') {
    const out: Record<string, unknown> = {}
    for (const [key, v] of Object.entries(value as Record<string, unknown>)) out[key] = deepCopy(v)
    return out as T
  }
  return value
}

/** What goes to `PUT /pages/{id}/blocks` — the list, in order, without the
 *  editor's own keys. */
export function toPayload(
  blocks: EditorBlock[],
): { type: BlockType; props: Record<string, unknown> }[] {
  return blocks.map((b) => ({ type: b.type, props: b.props }))
}

// -- the picker's one string -------------------------------------------------

/**
 * **Spec §9-41(b), and it is one string.** The owner's library calls this
 * section *Leadership*; the stored type is `team` and stays `team`, because a
 * stored type is an interface and renaming it would un-choose every block a
 * firm has already placed (ADR-0022 §2). The spec puts the label here rather
 * than in `BLOCK_SCHEMAS` in as many words: *"the label is in the builder's
 * picker, not in `BLOCK_SCHEMAS`, and nothing else changes."*
 *
 * So this is an override of one label, not a second copy of the vocabulary:
 * every other label, every description and every field still comes from the
 * service, and a type absent from this map falls through to the schema's own.
 */
const PICKER_LABELS: Partial<Record<BlockType, string>> = {
  team: 'Leadership',
}

export function sectionLabel(schema: BlockSchema | undefined, type: string): string {
  return PICKER_LABELS[type as BlockType] ?? schema?.label ?? type
}

// -- what a firm cannot see by looking ---------------------------------------

/**
 * **Sections whose content is read when the page is drawn, not when it is
 * saved** (FR-I14 rule 3, T-4.34).
 *
 * A firm writes a `services` section, publishes, adds a service a fortnight
 * later and its website changes on its own. That is the intended behaviour and
 * it is also genuinely surprising, because every other section on the page says
 * what the firm typed into it. Nothing on the screen said so until this
 * session.
 *
 * **Three (five since C21), and the two near misses are deliberate.** `contact` resolves the
 * firm's own details and `lead_form` resolves the services its dropdown offers
 * — both at render, both in `_resolve_block`. Neither is here, because what
 * changes under them is not the section's *content*: a `lead_form` says the
 * same thing tomorrow, and a firm editing its own telephone number on the
 * Settings screen is not surprised to find the contact section carrying it.
 * The note is for the three sections whose words arrive from somewhere the
 * firm was not looking.
 */
export const LIVE_AT_RENDER: readonly BlockType[] = [
  'services',
  'product_spotlight',
  'blog_list',
  // C21 (ADR-0043 §2): `pricing` reads the catalogue as `product_spotlight`
  // does, and `hours` reads *Your firm*'s week — a section with no content of
  // its own to type, which is exactly when a firm most needs telling where
  // its words come from.
  'pricing',
  'hours',
]

/** What reads live, and from where — the sentence rather than the flag, because
 *  "this changes on its own" without naming the source is alarming rather than
 *  informative. */
export function liveDataNote(type: string): string | null {
  switch (type) {
    case 'hours':
      return 'Reads your working hours from Your firm when a visitor opens the page, and marks today. Change your hours there and this section changes with it — you do not have to publish again.'
    case 'services':
    case 'product_spotlight':
    case 'pricing':
      return 'Reads your service catalogue when a visitor opens the page. Change a service and this section changes with it — you do not have to publish again.'
    case 'blog_list':
      return 'Reads your published posts when a visitor opens the page. Publish a post and it appears here — you do not have to publish the site again.'
    default:
      return null
  }
}

/**
 * **A section that will draw nothing, said where the section is chosen** —
 * Wave 8H, finding 3.
 *
 * The two sections that read the service catalogue render `null` when the
 * catalogue hands them no row, which is deliberate and right — *"renders
 * nothing rather than something wrong"* (ADR-0022 §7, and the Wave 8C container
 * pass watched it happen on a live page). What it is not is **legible**. A firm
 * adds *Services*, saves, publishes, looks at its website, finds the section
 * simply absent, and the reason is three screens away on a `site_visible`
 * switch in the service catalogue. The UAT firm's words were *"services added
 * but not shown in the services section"*, and that firm's eight services were
 * all visible — which is the point: when the section draws nothing there is no
 * way to tell from the website why.
 *
 * Said in the **builder** and nowhere else. The public page goes on rendering
 * nothing: a visitor told that this firm has no services is worse than a page
 * one section shorter.
 *
 * `null` means *we did not find out* — a role without `service:can_view`, or a
 * read that failed — and says nothing at all. That is the opposite choice from
 * `suppression`'s fail-closed, because the risks are opposite: a wrongly marked
 * suppression hides content a regulator may forbid, while a wrongly marked
 * empty catalogue tells a firm with eight services that it has none.
 */
export const CATALOGUE_SECTIONS: readonly BlockType[] = ['services', 'product_spotlight', 'pricing']

export function emptyCatalogueNote(type: string, visibleServices: number | null): string | null {
  if (visibleServices === null || visibleServices > 0) return null
  if (!CATALOGUE_SECTIONS.includes(type as BlockType)) return null
  const remedy =
    type === 'product_spotlight'
      ? 'Turn “show on the website” on for the service you want to feature.'
      : type === 'pricing'
        ? 'Turn “show on the website” on for the services whose plans you want side by side.'
      : 'Add a service, or turn “show on the website” on for one you already have.'
  return `None of your services is set to show on your website, so this section will draw nothing. ${remedy}`
}

/**
 * Whether **this firm's** template draws this section in its own markup
 * (T-4.34, ADR-0020 §2).
 *
 * The wording matters and is why this returns a sentence. A template that names
 * no section is not drawing sections badly: it restyles all twenty-two through
 * its own CSS, which is most of the eight and is the property FR-I14's theme
 * rule guarantees. So the note says *markup*, and there is no note at all for
 * the sections a template leaves to the shared renderer — a badge on eighteen
 * of twenty-two cards saying "ordinary" would be noise.
 */
export function themeNote(
  templateCode: string | null | undefined,
  templateName: string | null | undefined,
  type: string,
): string | null {
  if (!themeSections(templateCode).includes(type as BlockType)) return null
  return `${templateName || 'Your template'} draws this section in its own markup, so it will not look like the plain version.`
}

/**
 * A section the firm's conduct policy suppresses — shown, with its content, and
 * with the reason it will not render (ADR-0014 §3, T-4.34).
 *
 * ADR-0014 §3 has said since Wave 3 that a firm *"may write a testimonial block
 * and see it hidden, with the reason"*. The first half shipped in 8C: the
 * service drops the section from the render payload entirely. Without this half
 * a firm's testimonials simply vanish from its website and **nothing anywhere
 * says why** — which is worse than the switch not existing, because the firm
 * concludes the section is broken.
 *
 * The map is the service's (`vocabulary.policy.suppressed_by`) and so is the
 * resolved value of each switch (`GET /firm/policy`, whose `value` is already
 * the override over the profession's default), so this cannot disagree with
 * the render. It **fails closed** exactly as `_suppressed` does: a switch
 * nothing resolves is off, not on.
 */
export function suppression(
  type: string,
  vocabulary: Pick<Vocabulary, 'policy'> | null,
  switches: Record<string, boolean>,
): { switchName: string; reason: string } | null {
  const switchName = vocabulary?.policy?.suppressed_by?.[type]
  if (!switchName) return null
  if (switches[switchName] === true) return null
  return {
    switchName,
    reason:
      'Your conduct policy is keeping this section off your website. Nothing here is deleted — turn the switch on and it appears, with no other change.',
  }
}

/**
 * **Why this section will not appear, for the builder** — C24 item 11,
 * ADR-0049 §2, T-4.126.
 *
 * The owner's finding: *"in some pages componants/section able to add but it
 * not get visible in preview."* The editor showed the section, showed its
 * filled fields, and said nothing.
 *
 * It asks the **renderer's own question** — `site/blocks/visibility.ts:
 * whyHidden`, which calls the same `blockView` the page dispatches on — so the
 * notice and the page cannot drift. The builder's job here is only to assemble
 * the context that function needs out of what the editor holds.
 *
 * **Returns only the `empty` reason.** The `policy` one is `suppression`'s,
 * above: it has had its own notice and its own link to the conduct switches
 * since 8C, and two notices saying the same thing in different words would be
 * worse than one.
 */
export function emptyNote(
  type: string,
  props: Record<string, unknown>,
  switches: Record<string, boolean>,
  visibleServices: number | null,
): string | null {
  const reason = whyHidden(
    type,
    props,
    {
      // Enough context for the readers to read. The values are the permissive
      // ones: this question is about the firm's *content*, and the policy and
      // the catalogue each have a notice of their own.
      showPricing: true,
      portalHref: '/accounts/',
      enquiryAction: null,
      pagePath: '/',
      basePath: '',
      switches,
    } as BlockContext,
    visibleServices === null ? {} : { services: visibleServices },
  )
  return reason?.code === 'empty' ? reason.message : null
}

/**
 * `GET /firm/policy`'s switches as a plain map, **failing closed**.
 *
 * `value` is the effective answer the service already resolved — the firm's
 * override if it set one, the profession's shipped default otherwise — and it
 * is `null` for a firm that has not yet said which profession it is. `null`
 * becomes `false` here, because the one state that must never be guessed
 * optimistically is *"this firm's regulator may forbid it and we do not know"*.
 */
export function resolvedSwitches(
  switches: { switch: string; value: boolean | null }[] | null | undefined,
): Record<string, boolean> {
  const out: Record<string, boolean> = {}
  for (const row of switches ?? []) out[row.switch] = row.value === true
  return out
}

// -- the picker's groups (C21, Wave 21G, T-4.90) -----------------------------

/**
 * **The picker's groups** — thirty-eight sections in one flat grid is a
 * scroll, not a choice (ADR-0043 §7 says the same of the gallery). This is the
 * one list of them; the picker reads it and nothing else does.
 *
 * It is an editor's fact, not a description of a section: every label,
 * description and field still comes from the service. A type the service
 * serves and this list does not name is not lost — `pickerGroups` puts it
 * under *More*, so a thirty-ninth section is choosable the day it is served.
 * *Industry* is ADR-0043 §2's five display-only vertical sections.
 */
export const SECTION_GROUPS: readonly { key: string; label: string; types: readonly BlockType[] }[] = [
  { key: 'intro', label: 'Intro', types: ['hero', 'page_hero', 'statement'] },
  {
    key: 'content',
    label: 'Content',
    types: [
      'about',
      'rich_text',
      'split_feature',
      'feature_grid',
      'showcase',
      'steps',
      'tabs',
      'timeline',
      'faq',
      'blog_list',
      'image',
      'video',
      'before_after',
      'marquee',
    ],
  },
  {
    key: 'proof',
    label: 'Proof',
    types: ['metrics', 'team', 'logo_strip', 'testimonial_strip', 'story_strip', 'comparison'],
  },
  { key: 'offer', label: 'Offer', types: ['services', 'product_spotlight', 'pricing', 'cta'] },
  { key: 'contact', label: 'Contact', types: ['contact', 'lead_form', 'hours'] },
  { key: 'events', label: 'Events', types: ['countdown', 'schedule', 'tickets'] },
  {
    key: 'industry',
    label: 'Industry',
    types: ['price_list', 'courses', 'openings', 'goal', 'product_grid'],
  },
]

/** The group a type falls under when `SECTION_GROUPS` does not name it. */
export const OTHER_GROUP = { key: 'more', label: 'More' } as const

export interface PickerGroup {
  key: string
  label: string
  schemas: BlockSchema[]
}

/**
 * The schemas, grouped in `SECTION_GROUPS`' order and each group in its own
 * order. Empty groups are dropped (a search that matched none of them), and
 * any schema no group names is kept, under *More*, last.
 */
export function pickerGroups(schemas: readonly BlockSchema[]): PickerGroup[] {
  const byType = new Map(schemas.map((s) => [s.type as string, s]))
  const placed = new Set<string>()
  const out: PickerGroup[] = []
  for (const group of SECTION_GROUPS) {
    const found = group.types
      .map((t) => byType.get(t))
      .filter((s): s is BlockSchema => s !== undefined)
    found.forEach((s) => placed.add(s.type))
    if (found.length) out.push({ key: group.key, label: group.label, schemas: found })
  }
  const rest = schemas.filter((s) => !placed.has(s.type))
  if (rest.length) out.push({ ...OTHER_GROUP, schemas: [...rest] })
  return out
}
